import { useId } from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController } from 'react-hook-form';
import { SxProps } from '@mui/material';

import { IFileFragment } from 'graphql/types';
import { FieldLabel } from 'common/components/FieldLabel';
import { ActionButton } from 'common/components/ActionButton';
import { Icon } from 'common/components/Icon';
import { TControllerProps, EImageVariant } from 'common/types';
import {
    ImageUploader,
    ImageUploaderWithDefaults,
} from 'common/components/ImageUploader';
import { asTypedControl } from 'common/utils/form';

const imageFieldStyle: SxProps = {
    '.MuiFormHelperText-root': {
        fontWeight: 700,
        color: 'grey.500',
        marginTop: 1,
    },
};

interface IProps {
    name: string;
    className?: string;
    helperText?: string;
    label?: string;
    withDefaults?: boolean;
    maxUploadSize: number;
    acceptedFileTypes?: string;
    startSlide?: number;
    enableCropping?: boolean;
    imageVariant?: EImageVariant;
    onChange?(name: string, file: IFileFragment | null): void;
    onLoaded?(): void;
}

export const ImageField = <TFieldValues extends FieldValues>({
    control: formControl,
    helperText,
    label,
    withDefaults,
    className,
    name,
    startSlide,
    enableCropping = true,
    imageVariant,
    onChange,
    onLoaded,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const uniqueId = useId();
    const inputId = `${name}-${uniqueId}`;
    const labelId = `${name}-label-${uniqueId}`;

    const control = asTypedControl<IFileFragment>(name, formControl);
    const { field } = useController({ name, control });
    const { value } = field;
    const { onChange: fieldOnChange } = field;

    const handleChange = (newValue: IFileFragment | null) => {
        fieldOnChange(newValue);

        onChange && onChange(name, newValue);
    };

    return (
        <Box sx={imageFieldStyle}>
            <Box sx={{ height: '150px', position: 'relative' }}>
                {!withDefaults && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1,
                        }}
                    >
                        <ActionButton
                            aria-label={translate('removeImage')}
                            color="whiteTransparent"
                            size="medium"
                            onClick={() => handleChange(null)}
                        >
                            <Icon path={mdiClose} size="2.4rem" />
                        </ActionButton>
                    </Box>
                )}

                {!!label && (
                    <FieldLabel id={labelId}>
                        <Typography>{label}</Typography>
                    </FieldLabel>
                )}

                {withDefaults ? (
                    <ImageUploaderWithDefaults
                        aria-labelledby={labelId}
                        enableCropping={enableCropping}
                        id={inputId}
                        image={value}
                        imageVariant={imageVariant}
                        startSlide={startSlide}
                        onChange={handleChange}
                        onLoaded={onLoaded}
                        {...other}
                    />
                ) : (
                    <ImageUploader
                        aria-labelledby={labelId}
                        enableCropping={enableCropping}
                        id={inputId}
                        image={value}
                        imageVariant={imageVariant}
                        onUpload={handleChange}
                        {...other}
                    />
                )}
            </Box>
            {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
    );
};
